import { useEffect, useState } from 'react';
import { api } from '../../client/api';
import { ContentSkeleton } from '../../components/skeleton/ContentSkeleton';
import type {
  BoImpersonationIssued,
  BoImpersonationSession,
  BoTenant,
  ImpersonationScope,
} from '../../client/api';
import { cn } from '../../utils/cn';
import {
  BoPage,
  Section,
  ErrorBanner,
  btnGhost,
  btnPrimary,
  errMsg,
  formatDateTime,
  inputCls,
} from './ui';

// =============================================================================
// BO · Impersonation console (NBL_REQ_807 / HNBL-115) — issue and end
// time/scope-limited tenant sessions. Security: the raw token is returned by
// the backend exactly once at issue time; we surface it in a one-time reveal
// panel and never re-fetch it. Sessions always carry an expiry + scope.
// =============================================================================

const TTL_OPTIONS = [
  { label: '15분', ms: 15 * 60 * 1000 },
  { label: '30분', ms: 30 * 60 * 1000 },
  { label: '1시간', ms: 60 * 60 * 1000 },
  { label: '2시간 (최대)', ms: 2 * 60 * 60 * 1000 },
];

function remaining(expiresAt: string, now: number): string {
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return '만료됨';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min}분 남음`;
  return `${Math.floor(min / 60)}시간 ${min % 60}분 남음`;
}

/** One-time token reveal — disappears once dismissed; cannot be re-shown. */
function TokenReveal({
  issued,
  onDismiss,
}: {
  issued: BoImpersonationIssued;
  onDismiss: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-2 rounded border border-accent-ring bg-accent-soft p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[14px] font-semibold text-accent">가장 토큰 발급됨 — 한 번만 표시됩니다</span>
        <button type="button" className={btnGhost} onClick={onDismiss}>닫기</button>
      </div>
      <code className="break-all rounded border border-line bg-bg px-2.5 py-2 font-mono text-[13px] text-text">
        {issued.token}
      </code>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={btnPrimary}
          onClick={() => {
            void navigator.clipboard?.writeText(issued.token).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            });
          }}
        >
          {copied ? '복사됨' : '토큰 복사'}
        </button>
        <span className="text-[13px] text-text-dim">
          범위 {issued.scope === 'write' ? '쓰기' : '읽기'} · 만료 {formatDateTime(issued.expiresAt)}
        </span>
      </div>
      <p className="text-[13px] text-warning">
        이 토큰은 다시 표시할 수 없습니다. 지금 안전한 곳에 복사하세요.
      </p>
    </div>
  );
}

export function Impersonation(): JSX.Element {
  const [tenants, setTenants] = useState<BoTenant[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [sessions, setSessions] = useState<BoImpersonationSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [orgId, setOrgId] = useState('');
  const [scope, setScope] = useState<ImpersonationScope>('read');
  const [reason, setReason] = useState('');
  const [ttlMs, setTtlMs] = useState(TTL_OPTIONS[1]!.ms);
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<BoImpersonationIssued | null>(null);
  const [now, setNow] = useState(Date.now());

  async function loadSessions() {
    try {
      setSessions(await api.boListImpersonations());
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void (async () => {
      try {
        setTenants(await api.boListTenants());
      } catch (e) {
        setErr(errMsg(e));
      } finally {
        setLoadingTenants(false);
      }
    })();
    void loadSessions();
  }, []);

  // Tick once a minute so the "남은 시간" labels stay roughly current.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  async function handleIssue(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId) return;
    setIssuing(true);
    setErr(null);
    try {
      const trimmedReason = reason.trim();
      const result = await api.boIssueImpersonation({
        orgId,
        scope,
        ttlMs,
        ...(trimmedReason ? { reason: trimmedReason } : {}),
      });
      setIssued(result);
      setReason('');
      await loadSessions();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setIssuing(false);
    }
  }

  async function handleEnd(s: BoImpersonationSession) {
    if (!confirm('이 가장 세션을 종료하시겠습니까?')) return;
    setErr(null);
    try {
      await api.boEndImpersonation(s.id);
      await loadSessions();
    } catch (e) {
      setErr(errMsg(e));
    }
  }

  const tenantName = (id: string) => tenants.find((t) => t.id === id)?.name ?? id;

  return (
    <BoPage
      title="가장(Impersonation) 콘솔"
      subtitle="운영사 전용 — 만료·범위가 제한된 테넌트 가장 세션 발급·종료. 토큰은 발급 시 1회만 표시됩니다."
    >
      <ErrorBanner message={err} />
      {issued && <TokenReveal issued={issued} onDismiss={() => setIssued(null)} />}

      <Section title="세션 발급">
        {loadingTenants ? <ContentSkeleton label="세션 발급 정보를 불러오는 중…" variant="form" /> : (
        <form onSubmit={handleIssue} className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-[13px] font-medium text-text-dim">대상 조직 *</label>
              <select className={cn(inputCls, 'w-56')} value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                <option value="">조직 선택…</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[13px] font-medium text-text-dim">범위</label>
              <select className={cn(inputCls, 'w-32')} value={scope} onChange={(e) => setScope(e.target.value as ImpersonationScope)}>
                <option value="read">읽기 전용</option>
                <option value="write">쓰기 허용</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[13px] font-medium text-text-dim">유효 시간</label>
              <select className={cn(inputCls, 'w-36')} value={ttlMs} onChange={(e) => setTtlMs(Number(e.target.value))}>
                {TTL_OPTIONS.map((o) => <option key={o.ms} value={o.ms}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex flex-1 flex-col gap-1" style={{ minWidth: 240 }}>
              <label className="text-[13px] font-medium text-text-dim">사유 (선택, 감사 기록)</label>
              <input className={cn(inputCls, 'w-full')} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="고객 지원 #1234" />
            </div>
            <button type="submit" disabled={issuing || !orgId} className={btnPrimary}>
              {issuing ? '발급 중…' : '세션 발급'}
            </button>
          </div>
          <p className="text-[13px] text-text-faint">
            모든 세션은 만료되며(최대 2시간), 발급·종료가 대상 테넌트의 감사 로그에 기록됩니다.
          </p>
        </form>
        )}
      </Section>

      <Section title="활성 세션">
        {loading ? (
          <ContentSkeleton label="활성 가장 세션을 불러오는 중…" />
        ) : sessions.length === 0 ? (
          <p className="py-6 text-center text-[14px] text-text-faint">활성 가장 세션이 없습니다.</p>
        ) : (
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="border-b border-line bg-panel-2">
                  <th className="px-3 py-2 text-left font-semibold text-text-dim">대상 조직</th>
                  <th className="px-3 py-2 text-left font-semibold text-text-dim">운영자</th>
                  <th className="px-3 py-2 text-left font-semibold text-text-dim">범위</th>
                  <th className="px-3 py-2 text-left font-semibold text-text-dim">만료</th>
                  <th className="px-3 py-2 text-left font-semibold text-text-dim">사유</th>
                  <th className="px-3 py-2 text-right font-semibold text-text-dim"></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} className="border-b border-line last:border-0 hover:bg-panel-hover transition-colors">
                    <td className="px-3 py-2 text-text">{tenantName(s.targetOrgId)}</td>
                    <td className="px-3 py-2 text-text-dim">{s.operatorEmail}</td>
                    <td className="px-3 py-2">
                      <span className={cn('rounded px-1.5 py-0.5 text-[13px] font-medium', s.scope === 'write' ? 'bg-warning/10 text-warning' : 'bg-accent-soft text-accent')}>
                        {s.scope === 'write' ? '쓰기' : '읽기'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-text-dim">{formatDateTime(s.expiresAt)}</div>
                      <div className="text-[12px] text-text-faint">{remaining(s.expiresAt, now)}</div>
                    </td>
                    <td className="px-3 py-2 text-text-dim">{s.reason ?? <span className="text-text-faint">—</span>}</td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" className={cn(btnGhost, 'text-warning')} onClick={() => void handleEnd(s)}>종료</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </BoPage>
  );
}
